<template>
  <iq-card class="snapshot-card h-100 w-100">
    <div class="fms-card-header">
      <div>
        <h4>Today's Cruise Snapshot</h4>
        <p class="text-muted">Summary of vessel position & speed performance over the last 24 hours.</p>
      </div>
      <span class="fms-tag fms-tag--outline">24H Window</span>
    </div>
    <div class="snapshot-image">
      <img :src="assetImageSrc" :alt="assetImageAlt">
      <div class="snapshot-overlay">
        <div>
          <h5 class="text-white">{{ currAsset?.massName }}</h5>
          <div class="snapshot-meta">
            <span>Device Type: {{ currAsset?.massSystemType ?? '-' }}</span>
            <span>ID: {{ currAsset?.massId ?? '-' }}</span>
          </div>
        </div>
        <button class="detail-button" @click="$emit('detail', currAsset)">Detail</button>
      </div>
    </div>
    <div class="snapshot-stats">
      <div class="snapshot-stat">
        <div class="stat-header">
          <span>Current Coordinate</span>
          <span class="fms-pill fms-pill--blue">GPS</span>
        </div>
        <h5>{{ formatDms(currAssetLastData?.gps?.latitude, true) }}, {{ formatDms(currAssetLastData?.gps?.longitude, false) }}</h5>
        <p>{{ currentLocation || '-' }}</p>
      </div>
      <div class="snapshot-stat">
        <div class="stat-header">
          <span>Current Speed</span>
          <span class="fms-pill fms-pill--pink">Knot</span>
        </div>
        <h5>{{ currentSpeed }}</h5>
        <p>{{ currAssetLastData?.gps?.timestamp }}</p>
      </div>
      <div class="snapshot-stat">
        <div class="stat-header">
          <span>Total Cruise Today</span>
          <span class="fms-pill fms-pill--blue">NM</span>
        </div>
        <h5>{{ currAssetSummary?.gps?.totalDistance }}</h5>
        <p>24H Duration</p>
      </div>
      <div class="snapshot-stat">
        <div class="stat-header">
          <span>Average Speed</span>
          <span class="fms-pill fms-pill--pink">Knot</span>
        </div>
        <h5>{{ currAssetSummary?.gps?.averageSpeed }}</h5>
        <p>24H Duration</p>
      </div>
    </div>
  </iq-card>
</template>

<script>
import { kmhToKnot } from '@/util/units';
import { locationDescription } from '@/util/vesselDetail';
import { formatNum } from '@/util/number';

export default {
  name: 'TodayCruiseSnapshot',
  props: {
    currAsset: {
      type: Object,
      default: null,
    },
    currAssetLastData: {
      type: Object,
      default: null,
    },
    currAssetSummary: {
      type: Object,
      default: null,
    },
    deviceStatus: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  computed: {
    assetImageSrc() {
      if (!this.currAsset?.massImage) return 'https://placehold.co/600x325?text=No+Image';
      return `/api/${this.currAsset.massImage}`;
    },
    assetImageAlt() {
      return this.currAsset?.massName || 'No Image';
    },
    currentSpeed() {
      const speed = this.currAssetLastData?.gps?.speed;
      return speed ? formatNum(kmhToKnot(speed), 2) : '-';
    },
    currentLocation() {
      return this.currAsset ? locationDescription(this.currAsset) : null;
    },
  },
  methods: {
    formatDms(decimal, isLatitude) {
      if (typeof decimal === 'undefined' || decimal === null) return '-';
      const degrees = Math.floor(Math.abs(decimal));
      const minutesDecimal = (Math.abs(decimal) - degrees) * 60;
      const minutes = Math.floor(minutesDecimal);
      const seconds = Math.round((minutesDecimal - minutes) * 60);
      let direction = 'E';
      if (isLatitude) {
        direction = decimal >= 0 ? 'N' : 'S';
      } else {
        direction = decimal >= 0 ? 'E' : 'W';
      }
      return `${degrees}° ${minutes}' ${seconds}'' ${direction}`;
    },
  },
};
</script>

<style scoped>
.snapshot-card {
  padding: 15px 20px;
}

.snapshot-image {
  position: relative;
  margin-top: 15px;
  border-radius: 16px;
  overflow: hidden;
  height: 325px;
}

.snapshot-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.snapshot-overlay {
  position: absolute;
  bottom: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(20, 24, 36, 0.6);
  color: #fff;
  padding: 12px 16px;
  border-radius: 12px;
  backdrop-filter: blur(6px);
}

.snapshot-overlay h5 {
  margin: 0 0 4px;
  font-size: 1rem;
}

.snapshot-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  font-size: 0.6875rem;
}

.detail-button {
  border: none;
  background: #ffffff;
  color: #1d2433;
  font-size: 0.75rem;
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: 600;
}

.snapshot-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;
}

.snapshot-stat {
  background: #ffffff;
  border: 1px solid #E8ECF3;
  border-radius: 16px;
  padding: 8px 10px;
}

.snapshot-stat h5 {
  margin: 6px 0 2px;
  font-size: 0.875rem;
}

.snapshot-stat p {
  margin: 0;
  font-size: 0.75rem;
  color: #6b7280;
}

.stat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  color: #6b7280;
}

@media screen and (max-width: 991px) {
  .snapshot-stats {
    grid-template-columns: 1fr 1fr;
  }
}

@media screen and (max-width: 575px) {
  .snapshot-stats {
    grid-template-columns: 1fr;
  }
}
</style>
